import { Show, For, Switch, Match, createSignal, createMemo, onMount, onCleanup } from 'solid-js';
import type { CraftingResolverService } from '../services/craftingResolver';
import Text from './Text';
import MinecraftText from './MinecraftText';
import { useTranslation } from '../hooks/useTranslation';
import { VILLAGER_CAREER_KEYS } from '../services/villagerCareers';
import WindowSlot, { WindowSlotRow } from './inventory/WindowSlot';
import { isInventorySlotEmpty, type InventorySlot, type InventorySnapshot, type SlotRef } from '../models/inventorySlot';
import styles from './BotInventory.module.css';

interface BotInventoryProps {
  snapshot: InventorySnapshot | null;
  resolver: CraftingResolverService | null;
  interactive?: boolean;
  onMove?: (from: SlotRef, to: SlotRef) => void;
  onDrop?: (ref: SlotRef, wholeStack: boolean) => void;
  onCloseWindow?: () => Promise<void>;
}

// MC 1.8 player inventory: 0 output, 1-4 crafting, 5-8 armor, 9-35 main, 36-44 hotbar.
export const ARMOR_SLOTS = [5, 6, 7, 8];
export const MAIN_SLOTS = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35];
export const HOTBAR_SLOTS = [36, 37, 38, 39, 40, 41, 42, 43, 44];

// A 1.8 crafting table window: slot 0 is the result, 1-9 the 3x3 grid row by row.
const CRAFTING_TABLE_WINDOW = 'minecraft:crafting_table';
const CRAFTING_RESULT_SLOT = 0;
const CRAFTING_GRID_SLOTS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

// A 1.8 villager window: slots 0 and 1 take the payment, 2 holds what the selected offer sells.
const VILLAGER_WINDOW = 'minecraft:villager';
const MERCHANT_PAYMENT_SLOTS = [0, 1];
const MERCHANT_RESULT_SLOT = 2;

const sameRef = (a: SlotRef | null, b: SlotRef) => !!a && a.area === b.area && a.slot === b.slot;

export default function BotInventory(props: BotInventoryProps) {
  const { t } = useTranslation();
  const snap = () => props.snapshot;
  const openWindow = () => snap()?.window ?? null;

  // <For> diffs by array-item reference. Iterating w().slots directly would
  // remount every container slot on each SSE update, since the payload
  // rebuilds that array fresh each time. Index off a slot-count memo instead,
  // same as the static player-inventory slot arrays below.
  const windowSlotCount = createMemo(() => openWindow()?.slotCount ?? 0);
  const windowSlotIndices = createMemo(() => Array.from({ length: windowSlotCount() }, (_, i) => i));
  const isCraftingTable = createMemo(() => openWindow()?.type === CRAFTING_TABLE_WINDOW);
  const isVillager = createMemo(() => openWindow()?.type === VILLAGER_WINDOW);

  // The window type is a block id for every block-backed container, so the block's in-game name doubles as the window name.
  // A villager has no block; its window is titled with the career key, or with the villager's custom name.
  const windowName = createMemo(() => {
    const shownWindow = openWindow();
    if (!shownWindow?.type) return undefined;
    if (isVillager()) {
      const careerKey = VILLAGER_CAREER_KEYS[shownWindow.title];
      if (careerKey) return t(careerKey);
      return shownWindow.title || t('VILLAGER_TRADING_PROFESSION_UNKNOWN');
    }
    return props.resolver?.getMinecraftName(`${shownWindow.type}:0`);
  });

  const [dragged, setDragged] = createSignal<SlotRef | null>(null);
  const [dragOver, setDragOver] = createSignal<SlotRef | null>(null);
  const [hovered, setHovered] = createSignal<SlotRef | null>(null);
  const [closingWindow, setClosingWindow] = createSignal(false);

  const closeWindow = async () => {
    setClosingWindow(true);
    try {
      await props.onCloseWindow?.();
    } finally {
      setClosingWindow(false);
    }
  };

  const itemAt = (ref: SlotRef): InventorySlot | undefined =>
    ref.area === 'container' ? snap()?.window?.slots[ref.slot] : snap()?.slots[ref.slot];

  const isItemEmpty = isInventorySlotEmpty;

  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!props.interactive) return;
      if (e.key !== 'q' && e.key !== 'Q') return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const h = hovered();
      if (!h || isItemEmpty(itemAt(h))) return;
      e.preventDefault();
      props.onDrop?.(h, e.ctrlKey);
    };
    window.addEventListener('keydown', onKey);
    onCleanup(() => window.removeEventListener('keydown', onKey));
  });

  function Slot(slotProps: {
    slot: InventorySlot | undefined;
    active?: boolean;
    slotRef: SlotRef;
  }) {
    const empty = () => isItemEmpty(slotProps.slot);
    const canDrag = () => !!props.interactive && !empty();
    const isSource = () => sameRef(dragged(), slotProps.slotRef);
    const isTarget = () =>
      !!props.interactive && sameRef(dragOver(), slotProps.slotRef) && !isSource();

    return (
      <WindowSlot
        item={slotProps.slot}
        active={slotProps.active}
        interactive={props.interactive}
        dragging={isSource()}
        dropTarget={isTarget()}
        draggable={canDrag()}
        onDragStart={(e) => {
          if (!canDrag()) {
            e.preventDefault();
            return;
          }
          setDragged(slotProps.slotRef);
          if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
        }}
        onDragEnd={() => {
          setDragged(null);
          setDragOver(null);
        }}
        onDragOver={(e) => {
          if (!props.interactive || !dragged()) return;
          e.preventDefault();
          if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
          setDragOver(slotProps.slotRef);
        }}
        onDragLeave={() => {
          if (sameRef(dragOver(), slotProps.slotRef)) setDragOver(null);
        }}
        onDrop={(e) => {
          if (!props.interactive) return;
          e.preventDefault();
          const from = dragged();
          setDragged(null);
          setDragOver(null);
          if (from && !sameRef(from, slotProps.slotRef)) props.onMove?.(from, slotProps.slotRef);
        }}
        onMouseEnter={() => {
          if (props.interactive) setHovered(slotProps.slotRef);
        }}
        onMouseLeave={() => {
          if (sameRef(hovered(), slotProps.slotRef)) setHovered(null);
        }}
      />
    );
  }

  function RecipeLayout(layoutProps: { inputClass: string; inputSlots: number[]; resultSlot: number }) {
    return (
      <div class={styles.craftingLayout}>
        <div class={layoutProps.inputClass}>
          <For each={layoutProps.inputSlots}>
            {(slotIndex) => <Slot slot={openWindow()?.slots[slotIndex]} slotRef={{ area: 'container', slot: slotIndex }} />}
          </For>
        </div>
        <i class={`fa-solid fa-arrow-right ${styles.craftingArrow}`} />
        <Slot slot={openWindow()?.slots[layoutProps.resultSlot]} slotRef={{ area: 'container', slot: layoutProps.resultSlot }} />
      </div>
    );
  }

  return (
    <div class={`${styles.card} theme-force-dark`}>
      <h2>
        <i class="fa-solid fa-briefcase" /> <Text val="BOT_INVENTORY_TITLE" />
      </h2>

      <Show when={props.interactive}>
        <div class={styles.hint}>
          <Text val="BOTENGINE_INV_HINT" />
        </div>
      </Show>

      <Show
        when={snap()}
        fallback={<div style={{ color: 'var(--dark-text-muted)', 'font-size': '13px' }}><Text val="BOT_INVENTORY_WAITING" /></div>}
      >
        <Show when={openWindow()}>
          {(w) => (
            <>
              <div class={styles.section}>
                <div class={styles.windowHeader}>
                  <span>
                    <i class="fa-solid fa-box-open" /> <Show when={windowName()} fallback={<Text val="BOT_INVENTORY_CONTAINER" />}>{(name) => <MinecraftText text={name()} />}</Show>
                  </span>
                  <span class={styles.windowActions}>
                    <span class={styles.windowType}>{w().slotCount} <Text val="BOT_INVENTORY_SLOTS" /></span>
                    <Show when={props.interactive && props.onCloseWindow}>
                      <button class="btn btn-secondary btn-sm" onClick={closeWindow} disabled={closingWindow()}>
                        <i class={`fa-solid ${closingWindow() ? 'fa-spinner fa-spin' : 'fa-xmark'}`} /> <Text val="BOTENGINE_INV_CLOSE_WINDOW" />
                      </button>
                    </Show>
                  </span>
                </div>
                <Switch
                  fallback={
                    <WindowSlotRow>
                      <For each={windowSlotIndices()}>
                        {(idx) => <Slot slot={w().slots[idx]} slotRef={{ area: 'container', slot: idx }} />}
                      </For>
                    </WindowSlotRow>
                  }
                >
                  <Match when={isCraftingTable()}>
                    <RecipeLayout inputClass={styles.craftingGrid} inputSlots={CRAFTING_GRID_SLOTS} resultSlot={CRAFTING_RESULT_SLOT} />
                  </Match>
                  <Match when={isVillager()}>
                    <RecipeLayout inputClass={styles.merchantPayment} inputSlots={MERCHANT_PAYMENT_SLOTS} resultSlot={MERCHANT_RESULT_SLOT} />
                  </Match>
                </Switch>
              </div>
              <div class={styles.divider} />
            </>
          )}
        </Show>

        <div class={styles.section}>
          <div class={styles.sectionLabel}>
            <span><Text val="BOT_INVENTORY_ARMOR" /></span>
          </div>
          <WindowSlotRow armor>
            <For each={ARMOR_SLOTS}>
              {(i) => <Slot slot={snap()!.slots[i]} slotRef={{ area: 'player', slot: i }} />}
            </For>
          </WindowSlotRow>
        </div>

        <div class={styles.section}>
          <div class={styles.sectionLabel}>
            <span><Text val="BOT_INVENTORY_MAIN" /></span>
          </div>
          <WindowSlotRow>
            <For each={MAIN_SLOTS}>
              {(i) => <Slot slot={snap()!.slots[i]} slotRef={{ area: 'player', slot: i }} />}
            </For>
          </WindowSlotRow>
        </div>

        <div class={styles.section}>
          <div class={styles.sectionLabel}>
            <span><Text val="HOTBAR" /></span>
          </div>
          <WindowSlotRow>
            <For each={HOTBAR_SLOTS}>
              {(i, index) => (
                <Slot
                  slot={snap()!.slots[i]}
                  active={index() === snap()!.activeHotbar}
                  slotRef={{ area: 'player', slot: i }}
                />
              )}
            </For>
          </WindowSlotRow>
        </div>
      </Show>
    </div>
  );
}
